/**
 * Booking Prediction Service
 * Analyzes client booking patterns and predicts when clients are due to book
 * Enhanced with statistical methods for improved accuracy
 */

class BookingPredictionService {
  /**
   * Analyze booking pattern from opportunity history
   * @param {Array} opportunities - Array of opportunities sorted by CreatedDate
   * @returns {Object} Pattern analysis result
   */
  analyzeBookingPattern(opportunities) {
    // More lenient: allow single opportunity to show recent activity
    if (!opportunities || opportunities.length === 0) {
      return {
        pattern: 'insufficient_data',
        averageInterval: null,
        confidence: 'low',
        lastBookingDate: null,
        intervals: [],
        patternDescription: 'No data available'
      };
    }

    // Single opportunity - show recent activity but can't determine pattern
    if (opportunities.length === 1) {
      const singleOpp = opportunities[0];
      return {
        pattern: 'insufficient_data',
        averageInterval: null,
        confidence: 'low',
        lastBookingDate: new Date(singleOpp.CreatedDate),
        intervals: [],
        patternDescription: 'Recent activity (need 2+ opportunities for pattern)',
        totalOpportunities: 1
      };
    }

    // Sort by CreatedDate to ensure chronological order
    const sortedOpps = [...opportunities].sort((a, b) => {
      const dateA = new Date(a.CreatedDate);
      const dateB = new Date(b.CreatedDate);
      return dateA - dateB;
    });

    // Calculate intervals between consecutive opportunities
    const intervals = [];
    const intervalDates = []; // Track dates for trend analysis
    for (let i = 1; i < sortedOpps.length; i++) {
      const date1 = new Date(sortedOpps[i - 1].CreatedDate);
      const date2 = new Date(sortedOpps[i].CreatedDate);
      const daysDiff = Math.round((date2 - date1) / (1000 * 60 * 60 * 24));
      intervals.push(daysDiff);
      intervalDates.push({ start: date1, end: date2, interval: daysDiff });
    }

    if (intervals.length === 0) {
      return {
        pattern: 'insufficient_data',
        averageInterval: null,
        confidence: 'low',
        lastBookingDate: new Date(sortedOpps[sortedOpps.length - 1].CreatedDate),
        intervals: [],
        patternDescription: 'Insufficient data'
      };
    }

    // Detect and remove outliers using IQR method (only if we have enough data)
    const cleanedIntervals = intervals.length >= 4 
      ? this.removeOutliers(intervals) 
      : intervals;

    // Calculate basic statistics
    const sortedIntervals = [...cleanedIntervals].sort((a, b) => a - b);
    const median = this.median(sortedIntervals);
    const mean = cleanedIntervals.reduce((sum, val) => sum + val, 0) / cleanedIntervals.length;
    
    // Calculate standard deviation for better variance estimation
    const stdDev = this.calculateStandardDeviation(cleanedIntervals, mean);
    
    // Calculate weighted average (recent intervals weighted more heavily)
    const weightedAverage = this.calculateWeightedAverage(cleanedIntervals);
    
    // Detect trend (are intervals increasing, decreasing, or stable?)
    const trend = this.detectTrend(cleanedIntervals);
    
    // Detect seasonal patterns (day of week, month preferences)
    const seasonalPatterns = this.detectSeasonalPatterns(sortedOpps);

    // Use ensemble approach: combine median, weighted average, and trend-adjusted mean
    // Median is robust, weighted average captures recency, trend adjusts for direction
    let finalInterval = median;
    if (cleanedIntervals.length >= 3) {
      // Weighted combination: 40% median (robust), 35% weighted average (recency), 25% trend-adjusted
      const trendAdjustedMean = trend.direction === 'increasing' 
        ? mean * 1.1  // If increasing, expect slightly longer interval
        : trend.direction === 'decreasing'
        ? mean * 0.9  // If decreasing, expect slightly shorter interval
        : mean;
      
      finalInterval = (median * 0.4) + (weightedAverage * 0.35) + (trendAdjustedMean * 0.25);
    } else if (cleanedIntervals.length === 2) {
      // With only 2 intervals, use weighted average (more recent matters more)
      finalInterval = weightedAverage;
    }

    // Detect pattern based on final interval
    const pattern = this.detectPattern(finalInterval);
    
    // Enhanced confidence calculation considering multiple factors
    const confidence = this.calculateEnhancedConfidence(
      cleanedIntervals, 
      pattern, 
      finalInterval, 
      stdDev, 
      trend,
      sortedOpps.length
    );

    return {
      pattern,
      averageInterval: Math.round(finalInterval),
      meanInterval: Math.round(mean),
      medianInterval: Math.round(median),
      modeInterval: Math.round(this.mode(cleanedIntervals)),
      weightedAverage: Math.round(weightedAverage),
      standardDeviation: Math.round(stdDev * 10) / 10,
      confidence,
      lastBookingDate: new Date(sortedOpps[sortedOpps.length - 1].CreatedDate),
      intervals: cleanedIntervals,
      originalIntervals: intervals, // Keep original for reference
      trend,
      seasonalPatterns,
      patternDescription: this.getPatternDescription(pattern, finalInterval),
      totalOpportunities: sortedOpps.length,
      outlierCount: intervals.length - cleanedIntervals.length
    };
  }

  /**
   * Detect pattern type from interval
   * @param {number} interval - Days between bookings
   * @returns {string} Pattern type
   */
  detectPattern(interval) {
    // Check patterns in order of specificity (most specific first)
    // Monthly: 20-42 days (more lenient range, avoids overlap)
    if (interval >= 20 && interval < 42) {
      return 'monthly';
    }
    // Bi-monthly: 42-80 days (more lenient range)
    if (interval >= 42 && interval < 80) {
      return 'bi_monthly';
    }
    // Quarterly: 80-115 days (more lenient range)
    if (interval >= 80 && interval < 115) {
      return 'quarterly';
    }
    // Semi-annual: 160-210 days (more lenient range)
    if (interval >= 160 && interval < 210) {
      return 'semi_annual';
    }
    // Annual: 330-410 days (more lenient range)
    if (interval >= 330 && interval < 410) {
      return 'annual';
    }
    // Custom pattern for anything else
    return 'custom';
  }

  /**
   * Calculate enhanced confidence score considering multiple factors
   * @param {Array} intervals - Array of intervals
   * @param {string} pattern - Detected pattern
   * @param {number} averageInterval - Average interval
   * @param {number} stdDev - Standard deviation
   * @param {Object} trend - Trend analysis result
   * @param {number} sampleSize - Number of opportunities
   * @returns {string} Confidence level
   */
  calculateEnhancedConfidence(intervals, pattern, averageInterval, stdDev, trend, sampleSize) {
    if (intervals.length < 2) {
      return 'low';
    }

    // Factor 1: Sample size (more data = higher confidence)
    let sampleScore = 0;
    if (sampleSize >= 10) {
      sampleScore = 1.0;
    } else if (sampleSize >= 6) {
      sampleScore = 0.8;
    } else if (sampleSize >= 4) {
      sampleScore = 0.6;
    } else if (sampleSize >= 3) {
      sampleScore = 0.4;
    } else {
      sampleScore = 0.3;
    }

    // Factor 2: Consistency (coefficient of variation)
    const coefficientOfVariation = averageInterval > 0 ? stdDev / averageInterval : 1;
    const consistencyScore = Math.max(0, 1 - Math.min(coefficientOfVariation, 1)); // Lower CV = higher consistency

    // Factor 3: Pattern match (how well intervals fit the pattern)
    const tolerances = {
      monthly: 12,
      bi_monthly: 15,
      quarterly: 20,
      semi_annual: 30,
      annual: 45,
      custom: averageInterval * 0.4
    };

    const tolerance = tolerances[pattern] || averageInterval * 0.4;
    const lowerBound = averageInterval - tolerance;
    const upperBound = averageInterval + tolerance;

    const matchingIntervals = intervals.filter(
      interval => interval >= lowerBound && interval <= upperBound
    ).length;
    const patternMatchScore = matchingIntervals / intervals.length;

    // Factor 4: Trend stability (stable trends = higher confidence)
    const trendStabilityScore = trend.stability || 0.5;

    // Weighted combination of factors
    const confidenceScore = (
      sampleScore * 0.3 +           // Sample size: 30%
      consistencyScore * 0.35 +     // Consistency: 35%
      patternMatchScore * 0.25 +    // Pattern match: 25%
      trendStabilityScore * 0.1     // Trend stability: 10%
    );

    // Convert to confidence level
    if (confidenceScore >= 0.75) {
      return 'high';
    } else if (confidenceScore >= 0.55) {
      return 'medium';
    } else {
      return 'low';
    }
  }

  /**
   * Calculate confidence score based on interval consistency (legacy method for backward compatibility)
   * @param {Array} intervals - Array of intervals
   * @param {string} pattern - Detected pattern
   * @param {number} median - Median interval
   * @returns {string} Confidence level
   */
  calculateConfidence(intervals, pattern, median) {
    return this.calculateEnhancedConfidence(intervals, pattern, median, median * 0.2, { stability: 0.5 }, intervals.length);
  }

  /**
   * Predict next booking date based on pattern
   * @param {Object} clientData - Client data with pattern analysis
   * @returns {Object} Prediction result
   */
  predictNextBooking(clientData) {
    if (!clientData.patternAnalysis || clientData.patternAnalysis.pattern === 'insufficient_data') {
      return {
        predictedDate: null,
        predictedDateRange: null,
        daysUntilDue: null,
        isPredictable: false
      };
    }

    const { 
      lastBookingDate, 
      averageInterval, 
      pattern, 
      confidence,
      standardDeviation,
      trend,
      seasonalPatterns
    } = clientData.patternAnalysis;
    
    if (!lastBookingDate || !averageInterval) {
      return {
        predictedDate: null,
        predictedDateRange: null,
        daysUntilDue: null,
        isPredictable: false
      };
    }

    const lastDate = new Date(lastBookingDate);
    
    // Apply trend adjustment to prediction
    let adjustedInterval = averageInterval;
    if (trend && trend.direction === 'increasing' && trend.strength > 0.3) {
      // If intervals are consistently increasing, add a small buffer
      adjustedInterval = averageInterval * (1 + Math.min(trend.strength * 0.1, 0.15));
    } else if (trend && trend.direction === 'decreasing' && trend.strength > 0.3) {
      // If intervals are consistently decreasing, subtract a small buffer
      adjustedInterval = averageInterval * (1 - Math.min(trend.strength * 0.1, 0.15));
    }

    // Apply seasonal adjustment if pattern detected
    if (seasonalPatterns && seasonalPatterns.hasPattern) {
      const now = new Date();
      const daysSinceLastBooking = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
      const currentMonth = now.getMonth();
      
      // If we're in a preferred month and close to expected interval, slightly reduce prediction
      if (seasonalPatterns.preferredMonths.includes(currentMonth) && daysSinceLastBooking >= averageInterval * 0.8) {
        adjustedInterval = adjustedInterval * 0.95; // Slightly earlier prediction
      }
    }

    const predictedDate = new Date(lastDate);
    predictedDate.setDate(predictedDate.getDate() + Math.round(adjustedInterval));

    // Calculate variance for date range using standard deviation if available
    let variance = this.getVarianceForPattern(pattern, averageInterval);
    
    // If we have standard deviation, use it for more accurate variance
    if (standardDeviation !== undefined && standardDeviation > 0) {
      // Use 1.5 * stdDev for range (covers ~87% of cases for normal distribution)
      variance = Math.max(variance, Math.round(standardDeviation * 1.5));
    }
    
    // Adjust variance based on confidence
    if (confidence === 'low') {
      variance = Math.round(variance * 1.3); // Wider range for low confidence
    } else if (confidence === 'high') {
      variance = Math.round(variance * 0.85); // Narrower range for high confidence
    }

    const rangeStart = new Date(predictedDate);
    rangeStart.setDate(rangeStart.getDate() - variance);
    const rangeEnd = new Date(predictedDate);
    rangeEnd.setDate(rangeEnd.getDate() + variance);

    const now = new Date();
    const daysUntilDue = Math.ceil((predictedDate - now) / (1000 * 60 * 60 * 24));

    return {
      predictedDate,
      predictedDateRange: { start: rangeStart, end: rangeEnd },
      daysUntilDue,
      isPredictable: true,
      adjustedInterval: Math.round(adjustedInterval),
      confidence
    };
  }

  /**
   * Remove outliers using IQR (Interquartile Range) method
   * @param {Array} intervals - Array of intervals
   * @returns {Array} Intervals with outliers removed
   */
  removeOutliers(intervals) {
    if (intervals.length < 4) {
      return intervals; // Need at least 4 points for IQR
    }

    const sorted = [...intervals].sort((a, b) => a - b);
    const q1Index = Math.floor(sorted.length * 0.25);
    const q3Index = Math.floor(sorted.length * 0.75);
    const q1 = sorted[q1Index];
    const q3 = sorted[q3Index];
    const iqr = q3 - q1;
    
    // Use 1.5 * IQR as the outlier threshold (standard statistical practice)
    const lowerBound = q1 - (1.5 * iqr);
    const upperBound = q3 + (1.5 * iqr);

    return intervals.filter(interval => interval >= lowerBound && interval <= upperBound);
  }

  /**
   * Calculate standard deviation
   * @param {Array} intervals - Array of intervals
   * @param {number} mean - Mean value
   * @returns {number} Standard deviation
   */
  calculateStandardDeviation(intervals, mean) {
    if (intervals.length < 2) return 0;
    
    const variance = intervals.reduce((sum, val) => {
      return sum + Math.pow(val - mean, 2);
    }, 0) / intervals.length;
    
    return Math.sqrt(variance);
  }

  /**
   * Calculate weighted average with exponential decay (more recent = higher weight)
   * @param {Array} intervals - Array of intervals (most recent last)
   * @returns {number} Weighted average
   */
  calculateWeightedAverage(intervals) {
    if (intervals.length === 0) return 0;
    if (intervals.length === 1) return intervals[0];

    // Use exponential weighting: more recent intervals get exponentially higher weights
    let totalWeight = 0;
    let weightedSum = 0;
    
    // Base weight multiplier (each interval gets 1.2x more weight than the previous)
    const weightMultiplier = 1.2;
    
    intervals.forEach((interval, index) => {
      // Last interval (most recent) gets highest weight
      const weight = Math.pow(weightMultiplier, index);
      weightedSum += interval * weight;
      totalWeight += weight;
    });

    return weightedSum / totalWeight;
  }

  /**
   * Detect trend in intervals (increasing, decreasing, or stable)
   * @param {Array} intervals - Array of intervals (chronological order)
   * @returns {Object} Trend analysis result
   */
  detectTrend(intervals) {
    if (intervals.length < 3) {
      return {
        direction: 'stable',
        strength: 0,
        stability: 0.5
      };
    }

    // Use linear regression to detect trend
    const n = intervals.length;
    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    intervals.forEach((interval, index) => {
      const x = index + 1; // Time point (1, 2, 3, ...)
      const y = interval;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const meanY = sumY / n;
    
    // Calculate R-squared for trend strength
    let ssRes = 0;
    let ssTot = 0;
    intervals.forEach((interval, index) => {
      const x = index + 1;
      const predictedY = (slope * x) + (meanY - slope * (sumX / n));
      ssRes += Math.pow(interval - predictedY, 2);
      ssTot += Math.pow(interval - meanY, 2);
    });
    
    const rSquared = ssTot > 0 ? 1 - (ssRes / ssTot) : 0;
    const trendStrength = Math.min(Math.abs(rSquared), 1);

    // Determine direction
    let direction = 'stable';
    if (slope > 0.5) {
      direction = 'increasing';
    } else if (slope < -0.5) {
      direction = 'decreasing';
    }

    // Calculate stability (inverse of variance in residuals)
    const variance = ssRes / (n - 2);
    const stability = Math.max(0, Math.min(1, 1 - (variance / (meanY * meanY))));

    return {
      direction,
      strength: trendStrength,
      stability,
      slope: Math.round(slope * 100) / 100
    };
  }

  /**
   * Detect seasonal patterns (day of week, month preferences)
   * @param {Array} opportunities - Array of opportunities with CreatedDate
   * @returns {Object} Seasonal pattern analysis
   */
  detectSeasonalPatterns(opportunities) {
    if (opportunities.length < 4) {
      return { hasPattern: false, preferredMonths: [], preferredDaysOfWeek: [] };
    }

    const monthCounts = new Array(12).fill(0);
    const dayOfWeekCounts = new Array(7).fill(0);
    const total = opportunities.length;

    opportunities.forEach(opp => {
      const date = new Date(opp.CreatedDate);
      const month = date.getMonth();
      const dayOfWeek = date.getDay();
      monthCounts[month]++;
      dayOfWeekCounts[dayOfWeek]++;
    });

    // Find months with above-average booking frequency
    const avgMonthBookings = total / 12;
    const preferredMonths = [];
    monthCounts.forEach((count, month) => {
      if (count > avgMonthBookings * 1.2) { // 20% above average
        preferredMonths.push(month);
      }
    });

    // Find days of week with above-average booking frequency
    const avgDayBookings = total / 7;
    const preferredDaysOfWeek = [];
    dayOfWeekCounts.forEach((count, day) => {
      if (count > avgDayBookings * 1.2) { // 20% above average
        preferredDaysOfWeek.push(day);
      }
    });

    return {
      hasPattern: preferredMonths.length > 0 || preferredDaysOfWeek.length > 0,
      preferredMonths,
      preferredDaysOfWeek,
      monthDistribution: monthCounts,
      dayOfWeekDistribution: dayOfWeekCounts
    };
  }

  /**
   * Get variance days for pattern type
   * @param {string} pattern - Pattern type
   * @param {number} interval - Average interval
   * @returns {number} Variance in days
   */
  getVarianceForPattern(pattern, interval) {
    const variances = {
      monthly: 10,      // Increased from 7
      bi_monthly: 15,   // Increased from 10
      quarterly: 20,    // Increased from 15
      semi_annual: 30,  // Increased from 20
      annual: 45,       // Increased from 30
      custom: Math.max(10, Math.round(interval * 0.25)) // Increased from 0.2
    };
    return variances[pattern] || 10;
  }

  /**
   * Check if client is overdue for booking
   * @param {Object} clientData - Client data with pattern analysis
   * @param {Date} currentDate - Current date (defaults to now)
   * @returns {Object} Overdue status
   */
  isOverdue(clientData, currentDate = new Date()) {
    const prediction = this.predictNextBooking(clientData);
    
    if (!prediction.isPredictable) {
      return {
        isOverdue: false,
        daysOverdue: null,
        status: 'insufficient_data'
      };
    }

    const { predictedDate, predictedDateRange } = prediction;
    
    // Ensure dates are Date objects for proper comparison
    const current = currentDate instanceof Date ? currentDate : new Date(currentDate);
    const predicted = predictedDate instanceof Date ? predictedDate : new Date(predictedDate);
    
    const daysUntilDue = Math.ceil((predicted - current) / (1000 * 60 * 60 * 24));
    
    // Consider overdue if past the PREDICTED DATE (not the range start)
    // This ensures we only mark as overdue when actually past the prediction
    const daysPastPredicted = Math.ceil((current - predicted) / (1000 * 60 * 60 * 24));

    if (daysPastPredicted > 0) {
      return {
        isOverdue: true,
        daysOverdue: daysPastPredicted,
        daysUntilDue: -daysPastPredicted, // Negative since we're past due
        status: 'overdue'
      };
    } else if (daysUntilDue <= 7 && daysUntilDue >= 0) {
      // Approaching: within 7 days but not yet overdue
      return {
        isOverdue: false,
        daysOverdue: null,
        daysUntilDue,
        status: 'approaching'
      };
    } else {
      // On track: more than 7 days away
      return {
        isOverdue: false,
        daysOverdue: null,
        daysUntilDue,
        status: 'on_track'
      };
    }
  }

  /**
   * Detect if booking behavior has changed (enhanced with statistical analysis)
   * @param {Object} clientData - Client data with pattern analysis
   * @returns {Object} Pattern change detection result
   */
  detectPatternChange(clientData) {
    if (!clientData.patternAnalysis || 
        clientData.patternAnalysis.intervals.length < 4) {
      return {
        changeDetected: false,
        description: null,
        recentAverage: null,
        historicalAverage: null,
        significance: null
      };
    }

    const { intervals, trend } = clientData.patternAnalysis;
    
    // Use more sophisticated split: compare last 40% vs first 60% for better statistical power
    const splitPoint = Math.max(2, Math.floor(intervals.length * 0.6));
    const recentIntervals = intervals.slice(-splitPoint);
    const historicalIntervals = intervals.slice(0, intervals.length - splitPoint);

    if (historicalIntervals.length < 2 || recentIntervals.length < 2) {
      return {
        changeDetected: false,
        description: null,
        recentAverage: null,
        historicalAverage: null,
        significance: null
      };
    }

    const recentAverage = recentIntervals.reduce((sum, val) => sum + val, 0) / recentIntervals.length;
    const historicalAverage = historicalIntervals.reduce((sum, val) => sum + val, 0) / historicalIntervals.length;

    // Calculate standard deviations for both groups
    const recentStdDev = this.calculateStandardDeviation(recentIntervals, recentAverage);
    const historicalStdDev = this.calculateStandardDeviation(historicalIntervals, historicalAverage);

    // Use t-test-like approach: compare means considering variance
    const pooledStdDev = Math.sqrt(
      ((recentIntervals.length - 1) * recentStdDev * recentStdDev + 
       (historicalIntervals.length - 1) * historicalStdDev * historicalStdDev) /
      (recentIntervals.length + historicalIntervals.length - 2)
    );

    const standardError = pooledStdDev * Math.sqrt(
      1 / recentIntervals.length + 1 / historicalIntervals.length
    );

    const tStatistic = standardError > 0 
      ? Math.abs(recentAverage - historicalAverage) / standardError 
      : 0;

    // Detect change if:
    // 1. Mean difference is > 25% (was 30%)
    // 2. AND t-statistic suggests significant difference (> 1.5, roughly p < 0.15)
    const meanDeviation = Math.abs((recentAverage - historicalAverage) / historicalAverage);
    const changeDetected = meanDeviation > 0.25 && tStatistic > 1.5;

    // Calculate significance level
    let significance = 'low';
    if (tStatistic > 2.5) {
      significance = 'high';
    } else if (tStatistic > 1.8) {
      significance = 'medium';
    }

    let description = null;
    if (changeDetected) {
      const percentChange = Math.round(((recentAverage - historicalAverage) / historicalAverage) * 100);
      if (recentAverage > historicalAverage * 1.25) {
        description = `Booking frequency decreased - intervals increased ${percentChange}% from ${Math.round(historicalAverage)} to ${Math.round(recentAverage)} days (${significance} significance)`;
      } else if (recentAverage < historicalAverage * 0.75) {
        description = `Booking frequency increased - intervals decreased ${Math.abs(percentChange)}% from ${Math.round(historicalAverage)} to ${Math.round(recentAverage)} days (${significance} significance)`;
      } else {
        description = `Booking pattern has changed - intervals shifted from ${Math.round(historicalAverage)} to ${Math.round(recentAverage)} days (${significance} significance)`;
      }
    }

    return {
      changeDetected,
      description,
      recentAverage: Math.round(recentAverage),
      historicalAverage: Math.round(historicalAverage),
      deviation: Math.round(meanDeviation * 100),
      significance,
      tStatistic: Math.round(tStatistic * 100) / 100,
      trendDirection: trend?.direction || 'stable'
    };
  }

  /**
   * Get pattern description text
   * @param {string} pattern - Pattern type
   * @param {number} interval - Average interval
   * @returns {string} Description
   */
  getPatternDescription(pattern, interval) {
    const descriptions = {
      monthly: 'Monthly (~30 days)',
      bi_monthly: 'Bi-monthly (~60 days)',
      quarterly: 'Quarterly (~90 days)',
      semi_annual: 'Semi-annual (~180 days)',
      annual: 'Annual (~365 days)',
      custom: `Custom pattern (~${Math.round(interval)} days)`,
      insufficient_data: 'Insufficient data'
    };
    return descriptions[pattern] || 'Unknown pattern';
  }

  /**
   * Calculate median of array
   * @param {Array} arr - Sorted array
   * @returns {number} Median value
   */
  median(arr) {
    const mid = Math.floor(arr.length / 2);
    return arr.length % 2 === 0 
      ? (arr[mid - 1] + arr[mid]) / 2 
      : arr[mid];
  }

  /**
   * Calculate mode of array
   * @param {Array} arr - Array of numbers
   * @returns {number} Mode value
   */
  mode(arr) {
    const frequency = {};
    let maxFreq = 0;
    let mode = arr[0];

    arr.forEach(val => {
      // Round to nearest 5 days for mode calculation
      const rounded = Math.round(val / 5) * 5;
      frequency[rounded] = (frequency[rounded] || 0) + 1;
      if (frequency[rounded] > maxFreq) {
        maxFreq = frequency[rounded];
        mode = rounded;
      }
    });

    return mode;
  }
}

const bookingPredictionService = new BookingPredictionService();
export default bookingPredictionService;
